<template>
	<div class="guest-chat">
		<!-- 第三方App内浏览器拦截引导 -->
		<div v-if="blocked" class="guest-blocked">
			<i class="el-icon-top-right"></i>
			<p class="guest-blocked-title">请在浏览器中打开</p>
			<p class="guest-blocked-desc">当前处于第三方App内置浏览器，无法建立会话。<br/>请点击右上角「···」或「≡」，选择『在浏览器打开』后重试。</p>
			<el-button size="small" type="primary" class="guest-blocked-btn" @click="retryFromBlocked">已在浏览器打开? 点此进入</el-button>
		</div>
		<!-- 加载中 -->
		<div v-else-if="loading" class="guest-loading">
			<i class="el-icon-loading"></i>
			<p>正在连接客服...</p>
		</div>
		<!-- 加载失败 -->
		<div v-else-if="loadError" class="guest-error">
			<i class="el-icon-warning-outline"></i>
			<p>{{ loadError }}</p>
			<el-button size="small" @click="init">重试</el-button>
		</div>
		<!-- 聊天界面 -->
		<div v-else class="guest-chat-main">
			<!-- 顶部栏(信息最小化:固定显示在线客服) -->
			<div class="guest-header">
				<div class="guest-header-info">
					<div class="guest-cs-avatar"><i class="el-icon-service"></i></div>
					<div class="guest-header-text">
						<div class="guest-header-name">在线客服</div>
						<div class="guest-header-status">
							<span class="guest-status-dot" :class="online ? 'online' : 'offline'"></span>
							{{ online ? '在线' : '连接中' }}
						</div>
					</div>
				</div>
			</div>
			<!-- 消息列表 -->
			<div class="guest-messages" ref="msgList" @scroll="onScroll">
				<div v-if="loadingMore" class="guest-load-more"><i class="el-icon-loading"></i></div>
				<div v-if="!messages.length && !loadingMore" class="guest-empty-tip">
					发送消息开始与客服对话
				</div>
				<div v-for="msg in messages" :key="msg.localId || msg.id" class="guest-msg-item" :class="{ 'mine': isMine(msg) }">
					<div class="guest-msg-bubble" :class="{ 'mine': isMine(msg) }">
						<span v-if="msg.type === 0" class="guest-msg-text" v-html="formatText(msg.content)"></span>
						<span v-else-if="msg.type === 1" class="guest-msg-image">
							<img :src="parseImageUrl(msg.content)" @click="previewImage(parseImageUrl(msg.content))" />
						</span>
						<span v-else-if="msg.type === 2" class="guest-msg-file">
							<div class="guest-file-card">
								<i class="el-icon-document"></i>
								<div class="guest-file-meta">
									<div class="guest-file-name">{{ parseFileData(msg.content).name || '文件' }}</div>
									<div class="guest-file-size">{{ formatFileSize(parseFileData(msg.content).size) }}</div>
								</div>
							</div>
							<div class="guest-file-actions">
								<a class="guest-file-btn" @click="openFile(parseFileData(msg.content).url)">打开</a>
								<a class="guest-file-btn" @click="saveFile(parseFileData(msg.content).url, parseFileData(msg.content).name)">保存</a>
							</div>
						</span>
						<span v-else class="guest-msg-text">[不支持的消息类型]</span>
					</div>
					<div class="guest-msg-time">{{ formatTime(msg.sendTime) }}</div>
				</div>
			</div>
			<!-- 输入栏 -->
			<div class="guest-input-bar">
				<button class="guest-img-btn" :disabled="sending" title="发送图片" @click="$refs.imgInput.click()">
					<i class="el-icon-picture-outline"></i>
				</button>
				<input type="file" ref="imgInput" accept="image/*" style="display:none" @change="onPickImage" />
				<button class="guest-img-btn" :disabled="sending" title="发送文件/PDF" @click="$refs.fileInput.click()">
					<i class="el-icon-folder-add"></i>
				</button>
				<input type="file" ref="fileInput" style="display:none" @change="onPickFile" />
				<div class="guest-input-wrap">
					<textarea
						ref="inputArea"
						v-model="inputText"
						class="guest-input"
						placeholder="输入消息..."
						@keydown.enter.exact.prevent="onSend"
						rows="1"
					></textarea>
				</div>
				<button class="guest-send-btn" :disabled="!inputText.trim() || sending" @click="onSend">
					{{ sending ? '...' : '发送' }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import HeadImage from '../components/common/HeadImage.vue'
import { guestRegister, loadOfflineMessage, sendPrivateMessage, loadHistoryMessage } from '../api/guestApi.js'
import { connect, onConnect, onMessage, onClose, close } from '../api/wssocket.js'
import nextSnowflakeId from '../api/snowflake.js'
import { MESSAGE_TYPE } from '../api/enums.js'

export default {
	name: 'GuestChat',
	components: { HeadImage },
	data() {
		return {
			blocked: false,
			loading: true,
			loadError: '',
			online: false,
			sending: false,
			loadingMore: false,
			inputText: '',
			messages: [],
			guestId: null,
			guestName: '',
			guestAvatar: '',
			salespersonId: null,
			salespersonName: '',
			salespersonAvatar: '',
			maxSeqNo: 0,
			minSeqNo: 0,
			hasMore: true,
			wsConnected: false
		}
	},
	methods: {
		// 判断是否为第三方App内置webview: 只拦App内嵌浏览器(微信/QQ客户端/支付宝等),
		// 独立浏览器App(含QQ浏览器MQQBrowser/Safari/Chrome/各安卓厂商浏览器)一律放行
		isThirdPartyBrowser() {
			const ua = (navigator.userAgent || '').toLowerCase()
			return /micromessenger|(^|\s)qq\/|alipayclient|dingtalk|aweme|bytedancewebview|weibo|lark|feishu|xweb/.test(ua)
		},
		// 浏览器持久化设备ID: 同一浏览器稳定不变, 不同设备天然不同,
		// 作为设备指纹的一部分避免同公网IP+同UA的多位客户被合并成同一访客
		getDeviceId() {
			try {
				let did = localStorage.getItem('guestDeviceId')
				if (!did) {
					did = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
					localStorage.setItem('guestDeviceId', did)
				}
				return did
			} catch (e) {
				return ''
			}
		},
		retryFromBlocked() {
			// 从引导页重新进入: 强制刷新确保拿到浏览器真实UA(规避webview缓存)
			if (!this.isThirdPartyBrowser()) {
				window.location.reload()
			} else {
				this.$message && this.$message.warning('当前仍在第三方App内,请选择『在浏览器打开』')
			}
		},
		async init() {
			this.loading = true
			this.loadError = ''
			// 重置会话状态: 防止复用组件实例(引导页返回/切换业务员)时混入上一会话消息
			this.messages = []
			this.maxSeqNo = 0
			this.minSeqNo = 0
			this.hasMore = true
			// 第三方App内置浏览器拦截: 引导去手机浏览器打开
			if (this.isThirdPartyBrowser()) {
				this.blocked = true
				this.loading = false
				return
			}
			try {
				const sid = this.$route.query.sid
				if (!sid) {
					this.loadError = '缺少客服参数'
					this.loading = false
					return
				}
				// 调用注册接口(带浏览器持久化设备ID, 防止同IP同型号多客户指纹合并)
				const data = await guestRegister(sid, this.getDeviceId())
				// 保存token
				sessionStorage.setItem('accessToken', data.accessToken)
				sessionStorage.setItem('refreshToken', '')
				// 保存信息
				this.guestId = data.guestId
				this.guestName = data.guestName
				this.guestAvatar = data.guestAvatar
				this.salespersonId = data.salespersonId
				this.salespersonName = data.salespersonName
				this.salespersonAvatar = data.salespersonAvatar
				this.loading = false
				// 连接WebSocket
				this.connectWs()
			} catch (e) {
				console.error('访客注册失败', e)
				this.loadError = (e && e.message) || '连接客服失败,请稍后重试'
				this.loading = false
			}
		},
		connectWs() {
			onConnect(() => {
				this.wsConnected = true
				this.online = true
				this.pullOfflineMessages()
			})
			onMessage((cmd, message) => {
				if (cmd === 2) {
					// 强制下线
					close(3000)
					this.loadError = '连接已断开'
					return
				}
				if (cmd === 3) {
					this.handlePrivateMessage(message)
				}
				if (cmd === 5) {
					// 系统消息: 业务员清空临时会话时实时清屏(客户无需刷新即看不到)
					this.handleSystemMessage(message)
				}
			})
			onClose((e) => {
				this.wsConnected = false
				this.online = false
				if (e.code !== 3000) {
					// 非主动关闭,尝试重连
					setTimeout(() => {
						if (!this.wsConnected) {
							connect(process.env.VUE_APP_WS_URL, sessionStorage.getItem('accessToken'))
						}
					}, 5000)
				}
			})
			connect(process.env.VUE_APP_WS_URL, sessionStorage.getItem('accessToken'))
		},
		async pullOfflineMessages() {
			try {
				const msgs = await loadOfflineMessage(0)
				if (msgs && msgs.length) {
					// 会话隔离: 离线接口返回该访客与所有业务员的消息,只保留当前业务员的,
					// 防止同一浏览器扫过多个业务员二维码时历史消息串台混合
					msgs.filter(m => m.sendId === this.salespersonId || m.recvId === this.salespersonId)
						.forEach(m => this.addMessage(m))
					this.sortMessages()
					this.$nextTick(() => this.scrollToBottom())
				}
				// 如果没有离线消息,尝试拉取历史消息
				if (!this.messages.length) {
					await this.loadMoreHistory()
				}
			} catch (e) {
				console.error('拉取离线消息失败', e)
			}
		},
		async loadMoreHistory() {
			if (this.loadingMore || !this.hasMore) return
			this.loadingMore = true
			try {
				const msgs = await loadHistoryMessage(this.salespersonId, this.minSeqNo)
				if (msgs && msgs.length) {
					msgs.forEach(m => this.addMessage(m))
					this.sortMessages()
					if (msgs.length < 20) this.hasMore = false
				} else {
					this.hasMore = false
				}
			} catch (e) {
				console.error('拉取历史消息失败', e)
			} finally {
				this.loadingMore = false
			}
		},
		handlePrivateMessage(message) {
			// 只处理与业务员的消息
			if (message.sendId !== this.salespersonId && message.recvId !== this.salespersonId) return
			// 忽略非文本/图片/文件类型的系统消息
			if (message.type !== 0 && message.type !== 1 && message.type !== 2 && message.type !== 10 && message.type !== 21) return
			// 检查是否已存在(去重)
			const exist = this.messages.find(m => m.id && message.id && m.id === message.id)
			if (exist) return
			// 如果是自己的发送回执,更新状态
			if (message.sendId === this.guestId) {
				const local = this.messages.find(m => m.localId === message.localId)
				if (local) {
					local.id = message.id
					local.status = message.status
					local.seqNo = message.seqNo
					return
				}
			}
			this.addMessage(message)
			this.sortMessages()
			this.$nextTick(() => this.scrollToBottom())
			// 标记已读
			if (message.sendId === this.salespersonId) {
				this.markRead(message.id)
			}
		},
		addMessage(msg) {
			// 去重
			if (msg.id && this.messages.find(m => m.id === msg.id)) return
			if (msg.localId && this.messages.find(m => m.localId === msg.localId)) return
			this.messages.push(msg)
			if (msg.seqNo) {
				if (!this.maxSeqNo || msg.seqNo > this.maxSeqNo) this.maxSeqNo = msg.seqNo
				if (!this.minSeqNo || msg.seqNo < this.minSeqNo) this.minSeqNo = msg.seqNo
			}
		},
		sortMessages() {
			this.messages.sort((a, b) => (a.sendTime || 0) - (b.sendTime || 0))
		},
		async onSend() {
			const text = this.inputText.trim()
			if (!text || this.sending) return
			this.sending = true
			const localId = nextSnowflakeId()
			const msg = {
				localId: localId,
				sendId: this.guestId,
				recvId: this.salespersonId,
				content: text,
				type: 0,
				status: -1,
				sendTime: Date.now()
			}
			this.addMessage(msg)
			this.sortMessages()
			this.inputText = ''
			// 移动端发送后收起软键盘(部分浏览器点发送不会自动失焦)
			if (this.$refs.inputArea) this.$refs.inputArea.blur()
			this.$nextTick(() => this.scrollToBottom())
			try {
				const vo = await sendPrivateMessage(localId, this.salespersonId, text, 0)
				msg.id = vo.id
				msg.status = vo.status
				msg.seqNo = vo.seqNo
				msg.sendTime = vo.sendTime
			} catch (e) {
				msg.status = -2
				console.error('发送失败', e)
			} finally {
				this.sending = false
			}
		},
		async onPickImage(e) {
			const file = e.target.files && e.target.files[0]
			e.target.value = ''
			if (!file || this.sending) return
			if (file.size > 10 * 1024 * 1024) {
				this.loadError = ''
				alert('图片不能超过10MB')
				return
			}
			this.sending = true
			const localId = nextSnowflakeId()
			const msg = {
				localId: localId,
				sendId: this.guestId,
				recvId: this.salespersonId,
				content: '',
				type: 1,
				status: -1,
				sendTime: Date.now()
			}
			try {
				// 先上传拿URL,再发图片消息
				const fd = new FormData()
				fd.append('file', file)
				fd.append('isPermanent', 'true')
				const up = await this.$http({ url: '/image/upload', method: 'post', data: fd })
				msg.content = JSON.stringify({ originUrl: up.originUrl, thumbUrl: up.thumbUrl || up.originUrl })
				this.addMessage(msg)
				this.sortMessages()
				this.$nextTick(() => this.scrollToBottom())
				const vo = await sendPrivateMessage(localId, this.salespersonId, msg.content, 1)
				msg.id = vo.id
				msg.status = vo.status
				msg.seqNo = vo.seqNo
				msg.sendTime = vo.sendTime
			} catch (err) {
				msg.status = -2
				this.addMessage(msg)
				console.error('图片发送失败', err)
			} finally {
				this.sending = false
			}
		},
		async onPickFile(e) {
			const file = e.target.files && e.target.files[0]
			e.target.value = ''
			if (!file || this.sending) return
			if (file.size > 20 * 1024 * 1024) {
				alert('文件不能超过20MB')
				return
			}
			this.sending = true
			const localId = nextSnowflakeId()
			const msg = {
				localId: localId,
				sendId: this.guestId,
				recvId: this.salespersonId,
				content: '',
				type: 2,
				status: -1,
				sendTime: Date.now()
			}
			try {
				// 先上传拿URL,再发文件消息(type=2)
				const fd = new FormData()
				fd.append('file', file)
				const url = await this.$http({ url: '/file/upload', method: 'post', data: fd })
				msg.content = JSON.stringify({ name: file.name, size: file.size, url: url })
				this.addMessage(msg)
				this.sortMessages()
				this.$nextTick(() => this.scrollToBottom())
				const vo = await sendPrivateMessage(localId, this.salespersonId, msg.content, 2)
				msg.id = vo.id
				msg.status = vo.status
				msg.seqNo = vo.seqNo
				msg.sendTime = vo.sendTime
			} catch (err) {
				msg.status = -2
				this.addMessage(msg)
				console.error('文件发送失败', err)
			} finally {
				this.sending = false
			}
		},
		handleSystemMessage(message) {
			// 业务员清空临时会话: type=55(GUEST_CHAT_CLEAR), content=业务员id
			if (!message || message.type !== 55) return
			const sid = message.content
			if (sid && String(sid) !== String(this.salespersonId)) return
			this.messages = []
			this.maxSeqNo = 0
			this.minSeqNo = 0
			this.hasMore = true
			this.$message && this.$message.info('客服已清空本次会话聊天记录')
		},
		parseFileData(content) {
			try {
				return JSON.parse(content) || {}
			} catch (e) {
				return { name: '文件', url: content }
			}
		},
		formatFileSize(size) {
			if (size === undefined || size === null || size === '') return ''
			if (size > 1024 * 1024) return Math.round(size / 1024 / 1024) + 'M'
			if (size > 1024) return Math.round(size / 1024) + 'KB'
			return size + 'B'
		},
		openFile(url) {
			if (url) window.open(url, '_blank')
		},
		saveFile(url, name) {
			if (!url) return
			const fname = name || '文件'
			const ua = navigator.userAgent
			// iOS/微信内置浏览器不兼容 a[download], 改用 fetch→blob 下载, 失败降级为打开预览
			const needBlobDownload = /iPad|iPhone|iPod/.test(ua)
				|| (/Macintosh/.test(ua) && 'ontouchend' in document)
				|| /MicroMessenger/i.test(ua)
			if (needBlobDownload) {
				fetch(url).then(res => {
					if (!res.ok) throw new Error('下载失败:' + res.status)
					return res.blob()
				}).then(blob => {
					const blobUrl = URL.createObjectURL(blob)
					const a = document.createElement('a')
					a.href = blobUrl
					a.download = fname
					document.body.appendChild(a)
					a.click()
					a.remove()
					setTimeout(() => URL.revokeObjectURL(blobUrl), 3000)
				}).catch(() => {
					window.open(url, '_blank')
				})
				return
			}
			const a = document.createElement('a')
			a.href = url
			a.download = fname
			document.body.appendChild(a)
			a.click()
			a.remove()
		},
		markRead(messageId) {
			// 尽力标记已读,失败不影响聊天
			try {
				this.$http({
					url: '/message/private/readed',
					method: 'put',
					params: { friendId: this.salespersonId, messageId: messageId }
				})
			} catch (e) {
				// ignore
			}
		},
		isMine(msg) {
			return msg.sendId === this.guestId
		},
		formatText(text) {
			if (!text) return ''
			// 转义HTML
			return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
				.replace(/\n/g, '<br/>')
		},
		parseImageUrl(content) {
			try {
				const obj = JSON.parse(content)
				return obj.originUrl || obj.thumbUrl || ''
			} catch (e) {
				return content
			}
		},
		previewImage(url) {
			if (url) window.open(url, '_blank')
		},
		formatTime(ts) {
			if (!ts) return ''
			const d = new Date(typeof ts === 'number' ? ts : parseInt(ts))
			const now = new Date()
			const hh = String(d.getHours()).padStart(2, '0')
			const mm = String(d.getMinutes()).padStart(2, '0')
			if (d.toDateString() === now.toDateString()) {
				return hh + ':' + mm
			}
			return (d.getMonth() + 1) + '/' + d.getDate() + ' ' + hh + ':' + mm
		},
		scrollToBottom() {
			const el = this.$refs.msgList
			if (el) el.scrollTop = el.scrollHeight
		},
		onScroll() {
			const el = this.$refs.msgList
			if (el && el.scrollTop < 50 && this.hasMore && !this.loadingMore) {
				const prevHeight = el.scrollHeight
				this.loadMoreHistory().then(() => {
					this.$nextTick(() => {
						if (el) el.scrollTop = el.scrollHeight - prevHeight
					})
				})
			}
		},
		autoResize() {
			const ta = this.$refs.inputArea
			if (ta) {
				ta.style.height = 'auto'
				ta.style.height = Math.min(ta.scrollHeight, 80) + 'px'
			}
		}
	},
	mounted() {
		this.init()
		// 引导页兼容: 用户按指引切到浏览器后返回/重新可见时, UA已非第三方则自动重进
		this.onVisibilityChange = () => {
			if (document.visibilityState === 'visible' && this.blocked && !this.isThirdPartyBrowser()) {
				this.blocked = false
				this.init()
			}
		}
		document.addEventListener('visibilitychange', this.onVisibilityChange)
	},
	beforeDestroy() {
		close(3000)
		document.removeEventListener('visibilitychange', this.onVisibilityChange)
	},
	watch: {
		inputText() {
			this.$nextTick(this.autoResize)
		}
	}
}
</script>

<style scoped>
.guest-chat {
	display: flex;
	flex-direction: column;
	height: 100vh;
	/* iOS Safari 100vh 包含地址栏/工具栏高度,导致输入栏被推出屏幕; dvh 为实际可见高度 */
	height: 100dvh;
	background: #f5f5f5;
}

/* 加载/错误状态 */
.guest-loading,
.guest-error {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: #999;
}

/* 第三方App内浏览器拦截引导页 */
.guest-blocked {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: #fff;
	padding: 0 32px;
	text-align: center;
}
.guest-blocked > i {
	font-size: 52px;
	color: #07c160;
	margin-bottom: 18px;
}
.guest-blocked-title {
	font-size: 18px;
	font-weight: 600;
	color: #303133;
	margin-bottom: 12px;
}
.guest-blocked-desc {
	font-size: 14px;
	color: #909399;
	line-height: 1.8;
}
.guest-blocked-btn {
	margin-top: 24px;
}

/* 在线客服固定头像 */
.guest-cs-avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: #07c160;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
}

/* 输入栏图片按钮 */
.guest-img-btn {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	border: none;
	background: transparent;
	color: #606266;
	font-size: 22px;
	cursor: pointer;
	padding: 0;
}
.guest-img-btn:disabled {
	color: #c0c4cc;
	cursor: not-allowed;
}
.guest-loading i,
.guest-error i {
	font-size: 32px;
	margin-bottom: 12px;
}
.guest-loading p {
	font-size: 14px;
}
.guest-error p {
	font-size: 14px;
	margin-bottom: 16px;
}

/* 聊天主界面 */
.guest-chat-main {
	display: flex;
	flex-direction: column;
	height: 100vh;
	height: 100dvh;
}

/* 顶部栏 */
.guest-header {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	padding: 10px 16px;
	background: #fff;
	border-bottom: 1px solid #ebeef5;
}
.guest-header-info {
	display: flex;
	align-items: center;
	gap: 10px;
}
.guest-header-text {
	display: flex;
	flex-direction: column;
}
.guest-header-name {
	font-size: 15px;
	font-weight: 600;
	color: #303133;
}
.guest-header-status {
	font-size: 12px;
	color: #909399;
	display: flex;
	align-items: center;
	gap: 4px;
}
.guest-status-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	display: inline-block;
}
.guest-status-dot.online {
	background: #07c160;
}
.guest-status-dot.offline {
	background: #c0c4cc;
}

/* 消息列表 */
.guest-messages {
	flex: 1;
	overflow-y: auto;
	padding: 12px 16px;
	-webkit-overflow-scrolling: touch;
}
.guest-load-more {
	text-align: center;
	padding: 8px;
	color: #c0c4cc;
}
.guest-empty-tip {
	text-align: center;
	color: #c0c4cc;
	font-size: 13px;
	padding: 40px 0;
}
.guest-msg-item {
	margin-bottom: 12px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}
.guest-msg-item.mine {
	align-items: flex-end;
}
.guest-msg-bubble {
	max-width: 75%;
	padding: 8px 12px;
	border-radius: 8px;
	font-size: 14px;
	line-height: 1.5;
	word-break: break-all;
	background: #fff;
	color: #303133;
}
.guest-msg-bubble.mine {
	background: #07c160;
	color: #fff;
}
.guest-msg-text {
	white-space: pre-wrap;
}
.guest-msg-image img {
	max-width: 200px;
	max-height: 200px;
	border-radius: 4px;
	cursor: pointer;
}
.guest-msg-file {
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 160px;
	max-width: 220px;
}
.guest-file-card {
	display: flex;
	align-items: center;
	gap: 10px;
}
.guest-file-card > i {
	font-size: 30px;
	color: inherit;
	flex-shrink: 0;
}
.guest-file-meta {
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.guest-file-name {
	font-size: 14px;
	color: inherit;
	word-break: break-all;
	line-height: 1.4;
}
.guest-file-size {
	font-size: 12px;
	color: inherit;
	opacity: 0.7;
	margin-top: 2px;
}
.guest-file-actions {
	display: flex;
	gap: 16px;
	margin-top: 2px;
}
.guest-file-btn {
	font-size: 13px;
	color: inherit;
	cursor: pointer;
	text-decoration: underline;
	opacity: 0.9;
}
.guest-msg-time {
	font-size: 11px;
	color: #c0c4cc;
	margin-top: 2px;
}

/* 输入栏 */
.guest-input-bar {
	flex-shrink: 0;
	display: flex;
	align-items: flex-end;
	gap: 8px;
	padding: 8px 12px;
	/* iPhone 底部安全区(刘海屏 Home 条) */
	padding-bottom: calc(8px + env(safe-area-inset-bottom));
	background: #fff;
	border-top: 1px solid #ebeef5;
}
.guest-input-wrap {
	flex: 1;
}
.guest-input {
	width: 100%;
	border: 1px solid #dcdfe6;
	border-radius: 6px;
	padding: 8px 12px;
	/* iOS 下 font-size<16px 聚焦时会自动缩放页面导致布局错乱 */
	font-size: 16px;
	resize: none;
	outline: none;
	line-height: 1.5;
	max-height: 80px;
	overflow-y: auto;
	font-family: inherit;
	box-sizing: border-box;
}
.guest-input:focus {
	border-color: #07c160;
}
.guest-send-btn {
	flex-shrink: 0;
	background: #07c160;
	color: #fff;
	border: none;
	border-radius: 6px;
	padding: 8px 20px;
	font-size: 14px;
	cursor: pointer;
	height: 36px;
}
.guest-send-btn:disabled {
	background: #a0c9a0;
	cursor: not-allowed;
}
.guest-send-btn:not(:disabled):active {
	background: #06ad56;
}
</style>
